import { CHAPTERS, BOOK_INFO } from "@/lib/index";
import { BookOpen, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";

interface TableOfContentsProps {
  activeChapter: string;
  onChapterClick: (id: string) => void;
}

export function TableOfContents({ activeChapter, onChapterClick }: TableOfContentsProps) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      setProgress(pct);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const allItems = [
    { id: "cover", label: "表紙・はじめに・目次", number: null },
    ...CHAPTERS.map((ch) => ({
      id: ch.id,
      label: ch.title,
      number: ch.number,
    })),
    { id: "closing", label: "著者からの一言・免責", number: null },
  ];

  return (
    <div className="h-full flex flex-col py-6 px-5">
      <div className="mb-6 pb-5 border-b border-border">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4 text-accent" />
          <span className="font-sans text-xs text-muted-foreground tracking-wider uppercase">
            {BOOK_INFO.publicationDate}
          </span>
        </div>
        <h1 className="font-sans text-base font-bold text-foreground leading-tight mb-1">
          {BOOK_INFO.title}
        </h1>
        <p className="font-sans text-xs text-muted-foreground leading-snug">
          {BOOK_INFO.author}
          <br />
          <span className="text-[10px]">{BOOK_INFO.authorTitle}</span>
        </p>
      </div>

      <nav className="flex-1 overflow-y-auto space-y-0.5 scrollbar-hide">
        {allItems.map((item) => {
          const isActive = activeChapter === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onChapterClick(item.id)}
              className={`w-full text-left flex items-start gap-2.5 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted/60 text-muted-foreground hover:text-foreground"
              }`}
            >
              {item.number !== null ? (
                <span
                  className={`font-sans text-[10px] font-bold shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-accent/10 text-accent"
                  }`}
                >
                  {item.number}
                </span>
              ) : (
                <span className="w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center">
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-white" : "bg-border"}`} />
                </span>
              )}
              <span className="font-sans text-xs leading-snug">
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="mt-5 pt-5 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="font-sans text-xs text-muted-foreground">読書進捗</span>
          <span className="font-sans text-xs font-semibold text-foreground">{progress}%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-accent rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {progress > 20 && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1.5"
          >
            <ChevronUp className="w-3.5 h-3.5" />
            <span className="font-sans">トップへ戻る</span>
          </button>
        )}
      </div>
    </div>
  );
}